import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';

interface Props {
  /** Whether the checkbox is checked. */
  checked: boolean;
  /** Called when the checkbox toggles. */
  onChange: (next: boolean) => void;
  /** Validation error to surface beneath the checkbox, if any. */
  error?: string | undefined;
  /**
   * Subject of the attestation — used in the label. Defaults to "this
   * visitor" but pass-creation forms can override with "this domestic
   * worker", "this courier", "this driver" for accuracy.
   */
  subject?: string;
}

/**
 * LawfulBasisAttestation — single-line attestation checkbox required when
 * a resident or employee submits a third party's contact details to the
 * Platform.
 *
 * Pairs with a `recordConsent` call (scope: 'visitor_data_lawful_basis')
 * after the parent mutation succeeds, so the attestation is preserved in
 * the immutable consent_events trail.
 *
 * KDPA / Terms of Service §6 — the inviting user is the data controller
 * for the visitor's submitted PII; this component captures the moment
 * they assert that lawful basis.
 */
export function LawfulBasisAttestation({
  checked,
  onChange,
  error,
  subject = 'this visitor',
}: Props) {
  return (
    <Field>
      <label className="flex items-start gap-3 text-sm">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onChange(v === true)}
          aria-invalid={!!error}
        />
        <span>
          I confirm that I am authorised to share {subject}&apos;s details for this visit.
        </span>
      </label>
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </Field>
  );
}
